/// <reference types="google.maps" />
import React, { ChangeEvent, FC, useEffect, useRef } from "react";
import {
  Input,
  InputGroup,
  InputLeftElement,
} from "@UI/Components/Input/Input";
import { FormLabel } from "@UI/Components/Form/Form";
import { LocationDot } from "@UI/Components/Icons/LocationDot";
import { QuestionFormControl } from "../QuestionFormControl/QuestionFormControl";
import { BaseQuestionProps } from "../types";
import { AddressAutocompleteStyles } from "./autocompleteStyles";
import { getGoogleMapsGlobal, useGoogleMapsScript } from "./useGoogleMapsScript";

export interface AddressPlaceDetails {
  formattedAddress: string;
  /** Street name — Google's `route`. */
  street?: string;
  /** House number — Google's `street_number`. */
  streetNumber?: string;
  city?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
}

export interface AddressQuestionProps extends BaseQuestionProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (place: AddressPlaceDetails) => void;
  /** Browser key for the Maps JS SDK; comes from `@config`. Without it the field stays a plain text input. */
  apiKey?: string;
  /** ISO 3166-1 alpha-2, e.g. `["be", "fr"]` — the countries suggestions may come from, as the legacy widget restricts them. Google caps this at five. */
  countryRestriction?: string | string[];
  /** Language the suggestions come back in, e.g. "fr". Defaults to the browser's. */
  language?: string;
  placeholder?: string;
}

/**
 * First of `types` the place carries, in preference order — Google names the
 * same part differently by country, so the fallbacks are not interchangeable
 * synonyms but the next-best thing when the primary type is absent.
 */
const getAddressComponent = (
  components: google.maps.GeocoderAddressComponent[] | undefined,
  types: string[],
  nameKey: "short_name" | "long_name" = "short_name"
): string | undefined =>
  types
    .map(
      (type) =>
        components?.find((component) => component.types.includes(type))?.[
          nameKey
        ]
    )
    .find(Boolean);

/** What a pick and a geocode both come back with — `PlaceResult` and `GeocoderResult` alike. */
interface IResolvedPlace {
  address_components?: google.maps.GeocoderAddressComponent[];
  formatted_address?: string;
  geometry?: { location?: google.maps.LatLng };
}

export const toAddressPlaceDetails = (
  place: IResolvedPlace,
  fallbackAddress: string
): AddressPlaceDetails => {
  const components = place.address_components;

  return {
    formattedAddress: place.formatted_address ?? fallbackAddress,
    street: getAddressComponent(components, ["route"], "long_name"),
    streetNumber: getAddressComponent(components, ["street_number"]),
    // Belgium and France put the town in `locality`; `postal_town` is what
    // the UK uses instead, and `sublocality` covers the places Google gives
    // neither.
    city: getAddressComponent(
      components,
      ["locality", "postal_town", "sublocality"],
      "long_name"
    ),
    postalCode: getAddressComponent(components, ["postal_code"]),
    latitude: place.geometry?.location?.lat(),
    longitude: place.geometry?.location?.lng(),
  };
};

/**
 * Google's widget still dispatches on the legacy `keyCode`, so a synthetic key
 * event has to carry it — `key` alone leaves the press unrecognised.
 */
const ARROW_DOWN_KEYDOWN: KeyboardEventInit = {
  key: "ArrowDown",
  keyCode: 40,
  which: 40,
  bubbles: true,
};
const ENTER_KEYDOWN: KeyboardEventInit = {
  key: "Enter",
  keyCode: 13,
  which: 13,
  bubbles: true,
};

/**
 * Wraps google.maps.places.Autocomplete on a plain text input, mirroring the field
 * extraction the legacy flow does in _further_informations_form.html.slim (lat/lng,
 * locality, postal_code short_names) — reimplemented here instead of the jQuery widget.
 */
export const AddressQuestion: FC<AddressQuestionProps> = ({
  label,
  value,
  onChange,
  onPlaceSelect,
  apiKey,
  countryRestriction,
  language,
  placeholder = "",
  isRequired = false,
  isInvalid = false,
  errorMessage = "",
  isDisabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const { isLoaded } = useGoogleMapsScript(apiKey, { language });

  // Depended on as a string, not as the prop: an array arriving inline would be
  // a new identity every render, and rebuilding the widget is precisely what
  // the effect below must not do.
  const restrictionKey = [countryRestriction ?? []].flat().join(",");

  // The callbacks arrive as inline arrows, so their identity changes on every
  // render. Reading them through a ref keeps them out of the effect's deps:
  // rebuilding the Autocomplete per keystroke would leave a dead `.pac-container`
  // behind each time — Google appends one to <body> per widget and never removes
  // it, so the dropdowns stack up.
  const callbacksRef = useRef({ onChange, onPlaceSelect });
  callbacksRef.current = { onChange, onPlaceSelect };

  useEffect(() => {
    const googleMaps = getGoogleMapsGlobal();
    const input = inputRef.current;
    if (!isLoaded || !googleMaps || !input) return undefined;

    const containersBefore = new Set(document.querySelectorAll(".pac-container"));

    /**
     * The dropdown this widget owns. Resolved per keypress rather than once:
     * Google appends the node when it first has something to show, not when the
     * widget is constructed. Diffing against the snapshot above is the only way
     * to tell it from any other widget's — the instance holds no reference to it.
     */
    const getOwnContainer = () =>
      Array.from(document.querySelectorAll<HTMLElement>(".pac-container")).find(
        (node) => !containersBefore.has(node)
      );

    /**
     * Enter on typed text takes the first suggestion. Left to Google, that same
     * keypress fires `place_changed` for the raw string — no components, no
     * geometry — which the screen's completeness check can only reject, so the
     * key would never do anything but raise an error.
     *
     * Bound before the Autocomplete is constructed, and so ahead of Google's own
     * keydown listener: listeners on the event's target run in registration
     * order, capture flag or not.
     */
    const handleEnter = (event: KeyboardEvent) => {
      // Mid-IME, Enter commits the composition rather than choosing anything.
      if (event.key !== "Enter" || event.isComposing) return;

      const container = getOwnContainer();
      // No list on screen — before the first query, or after a pick closed it.
      // The keypress then belongs to the form, which moves the flow on.
      if (!container || container.style.display === "none") return;
      // Nothing to take, or the arrow keys already chose a row and Google is
      // about to commit exactly the one the user highlighted.
      if (
        !container.querySelector(".pac-item") ||
        container.querySelector(".pac-item-selected")
      )
        return;

      // The real keypress is swallowed whole: its default action would submit
      // the surrounding form with an address Google has not resolved, and
      // letting it through to Google would fire that text-only `place_changed`.
      // The pair below replays it with the first row highlighted — being
      // synthetic they steer the widget without submitting the form, which the
      // browser only does for genuine key presses.
      event.preventDefault();
      event.stopImmediatePropagation();
      input.dispatchEvent(new KeyboardEvent("keydown", ARROW_DOWN_KEYDOWN));
      input.dispatchEvent(new KeyboardEvent("keydown", ENTER_KEYDOWN));
    };

    input.addEventListener("keydown", handleEnter);

    const autocomplete = new googleMaps.maps.places.Autocomplete(input, {
      // `address` rather than the broader `geocode`: a provider is sent to a
      // doorstep, so bare streets, towns and postcodes have no business in the
      // list. It also keeps the suggestions in step with the completeness check
      // the field runs on what comes back.
      types: ["address"],
      // Only the parts the extraction below reads. Left unset, a selection is
      // billed as a full Place Details lookup for data nothing here uses.
      fields: ["address_components", "formatted_address", "geometry.location"],
      componentRestrictions: restrictionKey
        ? { country: restrictionKey.split(",") }
        : undefined,
    });

    const handlePlaceChanged = () => {
      const details = toAddressPlaceDetails(
        autocomplete.getPlace(),
        inputRef.current?.value ?? ""
      );

      callbacksRef.current.onChange(details.formattedAddress);
      callbacksRef.current.onPlaceSelect?.(details);
    };

    autocomplete.addListener("place_changed", handlePlaceChanged);
    autocompleteRef.current = autocomplete;

    return () => {
      input.removeEventListener("keydown", handleEnter);
      googleMaps.maps.event.clearInstanceListeners(autocomplete);
      // `clearInstanceListeners` unbinds the widget but leaves its dropdown in
      // the DOM, so the node has to go by hand. Diffing against the snapshot
      // taken above is the only way to tell which one this widget added — the
      // Autocomplete instance holds no reference to it.
      document
        .querySelectorAll(".pac-container")
        .forEach((node) => !containersBefore.has(node) && node.remove());
      autocompleteRef.current = null;
    };
  }, [isLoaded, restrictionKey]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  return (
    <QuestionFormControl
      isRequired={isRequired}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      isDisabled={isDisabled}
    >
      <AddressAutocompleteStyles />
      {label && <FormLabel>{label}</FormLabel>}
      {/* 56px and a white fill come from the design — the field sits on the
          flow's cream page background, so it needs to read as a raised control
          rather than blend into it. The icon element matches the height so the
          pin stays centred. */}
      <InputGroup>
        <InputLeftElement pointerEvents="none" height="56px">
          <LocationDot />
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          isDisabled={isDisabled}
          autoComplete="off"
          height="56px"
          bg="white"
        />
      </InputGroup>
    </QuestionFormControl>
  );
};
